import { createSelector } from '@reduxjs/toolkit';

import { formatTimeStampDate } from '@/helpers/dateTime';
import { Campaign, CampaignStats, CampaignsReducerPropsType, ShareWidgetData } from '@/models/campaignModels';
import { ServerStatus } from '@/models/globalModels';
import { MetricDataPoint, StatsMetric } from '@/models/statsModels';

import { RootState } from '../../rootReducer';
import * as t from '../types';

const globalState: CampaignsReducerPropsType = {
  campaigns: [],
  campaignsStatus: ServerStatus.IDLE,
  channelsStatus: ServerStatus.IDLE,
  enabledCampaignSelector: false,
  showInactiveCampaignStatus: false,
  showNoCampaignStatus: false,
  statsMetrics: undefined,
  statsMetricsStatus: ServerStatus.IDLE,
  statsStatus: ServerStatus.IDLE,
};

export type PossibleActions =
  | { type: 'CAMPAIGN_STATS_FETCH_ERROR'; payload: any }
  | { type: 'CAMPAIGN_STATS_FETCH'; payload: CampaignStats }
  | { type: 'CAMPAIGN_STATS_FETCHING' }
  | { type: 'SET_CAMPAIGNS_ERROR' }
  | { type: 'SET_CAMPAIGNS'; payload: Campaign[] }
  | { type: 'SET_INACTIVE_CAMPAIGN'; payload: string }
  | { type: 'SET_MULTIPLE_CAMPAIGNS' }
  | { type: 'SET_NO_CAMPAIGNS'; payload: string }
  | { type: 'SET_SELECTED_CAMPAIGN_ERROR' }
  | { type: 'SET_SELECTED_CAMPAIGN'; payload: Campaign }
  | { type: 'STATS_METRICS_FETCH_ERROR'; payload: any }
  | { type: 'STATS_METRICS_FETCH'; payload: StatsMetric[] }
  | { type: 'STATS_METRICS_FETCHING' }
  | { type: 'AMBASSADOR_STATS_FETCH_ERROR'; payload: any }
  | { type: 'AMBASSADOR_STATS_FETCH'; payload: CampaignStats }
  | { type: 'AMBASSADOR_STATS_FETCHING' };

const reducer = (state = globalState, action: PossibleActions): CampaignsReducerPropsType => {
  let error;
  switch (action.type) {
    case t.SET_CAMPAIGNS:
      const campaigns = Object.assign([], action.payload);
      return {
        ...state,
        campaigns: campaigns,
        campaignsStatus: ServerStatus.FETCH,
        showNoCampaignStatus: false,
        noCampaignMessage: '',
        enabledCampaignSelector: campaigns.length == 1 ? false : true,
      };
    case t.SET_CAMPAIGNS_ERROR:
      return {
        ...state,
      };
    case t.SET_SELECTED_CAMPAIGN:
      const selected = Object.assign({}, action.payload);
      return {
        ...state,
        selectedCampaign: selected,
        selectedCampaignId: selected.campaign_id,
        statsStatus: ServerStatus.IDLE,
        showNoCampaignStatus: false,
      };
    case t.SET_SELECTED_CAMPAIGN_ERROR:
      return {
        ...state,
      };
    case t.SET_MULTIPLE_CAMPAIGNS:
      return {
        ...state,
        enabledCampaignSelector: true,
        showNoCampaignStatus: false,
      };
    case t.SET_NO_CAMPAIGNS:
      return {
        ...state,
        showNoCampaignStatus: true,
        campaigns: [],
        selectedCampaign: undefined,
        selectedCampaignId: undefined,
        enabledCampaignSelector: false,
        noCampaignMessage: action.payload,
      };
    case t.SET_INACTIVE_CAMPAIGN:
      return {
        ...state,
        showInactiveCampaignStatus: true,
        noCampaignMessage: action.payload,
      };

    case t.CAMPAIGN_STATS_FETCHING:
      return {
        ...state,
        statsStatus: ServerStatus.FETCHING,
        campaignStats: undefined,
      };
    case t.CAMPAIGN_STATS_FETCH:
      const campaignStats = Object.assign({}, action.payload);
      return {
        ...state,
        statsStatus: ServerStatus.FETCH,
        campaignStats: campaignStats,
      };
    case t.CAMPAIGN_STATS_FETCH_ERROR:
      error = action.payload;
      return {
        ...state,
        statsStatus: ServerStatus.FETCH_ERROR,
        campaignStats: undefined,
        error: error,
      };

    case t.AMBASSADOR_STATS_FETCHING:
      return {
        ...state,
        statsStatus: ServerStatus.FETCHING,
        ambassadorStats: undefined,
      };
    case t.AMBASSADOR_STATS_FETCH:
      const data = Object.assign({}, action.payload);
      return {
        ...state,
        statsStatus: ServerStatus.FETCH,
        ambassadorStats: data,
      };
    case t.AMBASSADOR_STATS_FETCH_ERROR:
      error = action.payload;
      return {
        ...state,
        statsStatus: ServerStatus.FETCH_ERROR,
        ambassadorStats: undefined,
        error: error,
      };
    case t.STATS_METRICS_FETCHING:
      return {
        ...state,
        statsMetricsStatus: ServerStatus.FETCHING,
      };
    case t.STATS_METRICS_FETCH:
      const statsMetrics = Object.assign([], action.payload);
      const formattedMetrics = statsMetrics.map((metric: any) => {
        // converts raw API values (single array) to MetricDataPoint type for chart rendering
        const formattedValues = metric?.values?.map((value: any) => {
          return {
            time: formatTimeStampDate(value[0] as number),
            value: value[1] as number,
          };
        });
        return { ...metric, values: formattedValues as MetricDataPoint[] };
      }) as StatsMetric[];
      return {
        ...state,
        statsMetricsStatus: ServerStatus.FETCH,
        statsMetrics: formattedMetrics,
      };
    case t.STATS_METRICS_FETCH_ERROR:
      error = action.payload;
      return {
        ...state,
        statsMetricsStatus: ServerStatus.FETCH_ERROR,
        campaignStats: undefined,
        error: error,
      };
    default:
      return {
        ...state,
      };
  }
};

// SELECTORS
export const selectShareWidgetData = createSelector(
  (state: RootState) => state.campaigns.selectedCampaign,
  (campaign): ShareWidgetData =>
    campaign
      ? {
          campaign_name: campaign.campaign_name,
          campaign_description: campaign.campaign_description,
          is_active: campaign.is_active,
          url: campaign.url,
          uid: campaign.uid,
          campaign_id: campaign.campaign_id,
          native_message: campaign.native_message,
          short_code: campaign?.short_code!,
        }
      : ({} as ShareWidgetData),
);

export default reducer;
